import type { App } from 'vue';
import { markRaw, readonly, ref } from 'vue';

import { PLUGIN_KEY } from './providers';
import { createState } from './state';
import type { ModalEntry, ModalPlugin, PluginOptions } from './types';

function* uid() {
  let i = 0;

  while (true) {
    yield i++;
  }
}

export function createModalPlugin(options?: PluginOptions): ModalPlugin {
  const modals = ref<ReturnType<typeof createState>[]>([]);

  const config = readonly(options || Object.create(null));

  return markRaw({
    modals,

    config,

    loadAsyncModal(entry: ModalEntry) {
      if (modals.value.filter((modal) => modal.component).length > (options?.modalStackLimit ?? 2)) {
        throw new Error(
          `Trying to open too many nested modals. Check plugin config. MAX: ${options?.modalStackLimit ?? 2}`,
        );
      }

      const id = uid().next().value as number;
      const state = createState(id);

      modals.value.push(state);

      const loading = ref(true);

      (async () => {
        try {
          const component = await entry.component();

          state.open(component.default ? component.default : component, {
            close: state.close,
            onBeforeClose: (clb?: () => Promise<void> | void) => (state.onBeforeCloseClb = clb),
            onClosed: (clb?: () => void) => (state.onClosedClb = clb),
            ...entry.props,
          });
        } finally {
          loading.value = false;
        }
      })();

      return {
        close: state.close,
        isLoading: loading,
        onBeforeClose: (clb?: () => Promise<void> | void) => (state.onBeforeCloseClb = clb),
        onClosed: (clb?: () => void) => (state.onClosedClb = clb),
      };
    },

    install(app: App): void {
      app.provide(PLUGIN_KEY, this);
      app.config.globalProperties['$modal'] = this;
    },
  });
}
